<script lang="ts">
  import { onDestroy } from 'svelte';
  import type {
    PowerLevelLocation,
    RoomPermissionsView,
    RoomListItemView,
  } from '#src/generated/protocol';
  import CheckIcon from 'phosphor-svelte/lib/CheckIcon';
  import ListChecksIcon from 'phosphor-svelte/lib/ListChecksIcon';
  import PencilIcon from 'phosphor-svelte/lib/PencilIcon';
  import XIcon from 'phosphor-svelte/lib/XIcon';

  import { useCoreClient } from '#lib/core/context.js';
  import { i18n } from '#lib/i18n.js';
  import { nativeFilePicker } from '#lib/platform/native-file-picker.js';
  import { useRoomList } from '#lib/rooms/room-list.svelte.js';
  import Alert from '#lib/ui/primitives/Alert.svelte';
  import Button from '#lib/ui/primitives/Button.svelte';
  import ConfirmDialog from '#lib/ui/primitives/ConfirmDialog.svelte';
  import IconButton from '#lib/ui/primitives/IconButton.svelte';
  import FormField from '#lib/ui/primitives/FormField.svelte';
  import Select from '#lib/ui/primitives/Select.svelte';
  import SettingsRow from '#lib/ui/primitives/SettingsRow.svelte';
  import SettingsSection from '#lib/ui/primitives/SettingsSection.svelte';
  import Spinner from '#lib/ui/primitives/Spinner.svelte';
  import TextInput from '#lib/ui/primitives/TextInput.svelte';
  import { uprightJpeg } from '#lib/ui/upright-jpeg.js';

  import '#lib/ui/primitives/settings-row.css';

  import { ancestorSpaceIds, descendantRoomIds } from '../abbreviations.js';
  import MemberIdentityRow from '../members/MemberIdentityRow.svelte';
  import { FOUNDER_POWER_LEVEL } from '../members/power-tags';
  import ReactionPicker from '../messages/ReactionPicker.svelte';
  import RoleTagIcon from '../members/RoleTagIcon.svelte';
  import { canSendState, levelAt, permissionGroups } from './permission-groups';
  import {
    parsePowerLevelInput,
    POWER_LEVEL_TAGS_EVENT_TYPE,
    tagForLevel,
  } from './power-level-tags';
  import { MEMBER_LIST_EVENT_TYPE, memberListChanged } from './member-list.svelte.js';
  import { RoomPermissionsData } from './room-permissions-data.svelte';

  interface Props {
    room: RoomListItemView | null;
    permissions: RoomPermissionsView | null;
  }

  let { room, permissions }: Props = $props();
  const core = useCoreClient();
  const roomList = useRoomList();
  const data = new RoomPermissionsData(core);

  const namedLevels: readonly { level: number; label: string }[] = [
    { level: FOUNDER_POWER_LEVEL, label: 'timeline.powerTagFounder' },
    { level: 100, label: 'timeline.powerLevelAdmin' },
    { level: 50, label: 'timeline.powerLevelModerator' },
    { level: 0, label: 'timeline.powerLevelMember' },
  ];

  let levels = $derived(data.levels);
  let loading = $derived(data.loading);
  let failed = $derived(data.failed);
  let saving = $state(false);
  let roleTags = $derived(data.roleTags);
  let founders = $derived(data.founders);
  let peekLevel = $state<number | null>(null);
  let iconUploading = $state(false);
  let iconInput = $state<HTMLInputElement | null>(null);
  let roleLevels = $derived(
    [
      ...new Set([
        ...Object.keys(roleTags).map(Number),
        ...namedLevels.map((entry) => entry.level),
      ]),
    ].sort((left, right) => right - left)
  );

  let alwaysListedFrom = $derived(data.alwaysListedFrom);
  let memberListSaving = $state(false);
  let memberListFailed = $state(false);
  let memberListChoices = $derived([
    { value: 'default', label: i18n.t('room.memberListDefault') },
    ...[...new Set([...roleLevels, ...(alwaysListedFrom === null ? [] : [alwaysListedFrom])])]
      .filter((level) => level >= 0)
      .sort((left, right) => right - left)
      .map((level) => ({
        value: String(level),
        label: i18n.t('room.memberListFrom', { role: levelLabel(level) }),
      })),
  ]);

  let editingLevel = $state<number | null>(null);
  let editingNewRole = $state(false);
  let editingRole = $derived(editingNewRole || editingLevel !== null);
  let editingHasTag = $derived(
    editingLevel !== null && tagForLevel(roleTags, editingLevel) !== null
  );
  let roleLevelDraft = $state('');
  let roleLevelError = $state<string | null>(null);
  let roleNameDraft = $state('');
  let roleColorDraft = $state('');
  let roleIconDraft = $state('');
  let roleSaving = $state(false);
  let roleFailed = $state(false);
  let roleRemoveConfirm = $state(false);

  let roomId = $derived(room?.room_id ?? null);
  let groups = $derived(permissionGroups(room?.room_kind === 'space'));
  let canEdit = $derived(permissions?.can_change_power_levels ?? false);
  let pickedColor = $derived(
    /^#[0-9a-f]{6}$/i.test(roleColorDraft.trim()) ? roleColorDraft.trim() : undefined
  );
  let ownLevel = $derived(permissions?.own_power_level ?? 0);
  let canEditMemberList = $derived(canSendState(levels, ownLevel, MEMBER_LIST_EVENT_TYPE));
  let canEditRoles = $derived(canSendState(levels, ownLevel, POWER_LEVEL_TAGS_EVENT_TYPE));

  let syncChoice = $state<string | null>(null);
  let syncConfirm = $state(false);
  let syncing = $state(false);
  let syncFailed = $state(false);
  let syncSpaceIds = $derived(
    roomId && room?.room_kind !== 'space' ? ancestorSpaceIds(roomList.rooms, roomId).reverse() : []
  );
  let syncSpaceId = $derived(
    syncChoice !== null && syncSpaceIds.includes(syncChoice)
      ? syncChoice
      : (syncSpaceIds[0] ?? null)
  );

  let childIds = $derived(
    roomId && room?.room_kind === 'space' ? descendantRoomIds(roomList.rooms, roomId) : []
  );
  let childConfirm = $state(false);
  let childSyncing = $state(false);
  let childResult = $state<{ updated: number; skipped: number } | null>(null);

  $effect(() => {
    data.sync(roomId);
  });

  onDestroy(() => data.dispose());

  async function setLevel(location: PowerLevelLocation, level: number): Promise<void> {
    const target = roomId;
    const current = levels;
    if (!target || !current || saving) return;

    saving = true;
    data.failed = false;
    try {
      data.levels = await core.rpc.members.setPowerLevel({
        room_id: target,
        levels: $state.snapshot(current),
        location,
        level,
      });
    } catch (error) {
      console.warn('[sable room] permission change failed', error);
      data.failed = true;
    } finally {
      saving = false;
    }
  }

  function spaceName(spaceId: string): string {
    return roomList.byId(spaceId)?.name ?? spaceId;
  }

  async function syncFromSpace(): Promise<void> {
    const target = roomId;
    const spaceId = syncSpaceId;
    const current = levels;
    if (!target || !spaceId || !current || syncing) return;

    syncing = true;
    syncFailed = false;
    try {
      const next = await core.rpc.members.syncPowerLevelsFromSpace({
        room_id: target,
        space_id: spaceId,
        levels: $state.snapshot(current),
      });
      data.levels = next.levels;
      data.roleTags = next.tags;
      syncConfirm = false;
    } catch (error) {
      console.warn('[sable room] permission sync failed', error);
      syncFailed = true;
    } finally {
      syncing = false;
    }
  }

  async function syncChildren(): Promise<void> {
    const spaceId = roomId;
    if (!spaceId || childSyncing) return;

    childSyncing = true;
    try {
      childResult = await core.rpc.members.syncChildPowerLevels({
        space_id: spaceId,
        child_ids: childIds,
      });
    } catch (error) {
      console.warn('[sable room] child permission sync failed', error);
      childResult = { updated: 0, skipped: childIds.length };
    } finally {
      childSyncing = false;
      childConfirm = false;
    }
  }

  async function saveMemberList(value: string): Promise<void> {
    const target = roomId;
    if (!target) return;
    const next = value === 'default' ? null : Number(value);
    const previous = alwaysListedFrom;
    data.alwaysListedFrom = next;
    memberListSaving = true;
    memberListFailed = false;
    try {
      await core.rpc.room_state.setMemberListThreshold({
        room_id: target,
        always_listed_from: next,
      });
      memberListChanged();
    } catch (error) {
      console.warn('[sable room] member list settings not saved', error);
      data.alwaysListedFrom = previous;
      memberListFailed = true;
    } finally {
      memberListSaving = false;
    }
  }

  function levelLabel(level: number): string {
    const tag = tagForLevel(roleTags, level);
    if (tag) return tag.name;
    const known = namedLevels.find((entry) => entry.level === level);
    return known ? i18n.t(known.label) : String(level);
  }

  function options(current: number): { value: string; label: string; disabled?: boolean }[] {
    const named = namedLevels.map((entry) => ({
      value: String(entry.level),
      label: levelLabel(entry.level),
      disabled: entry.level > ownLevel,
    }));
    const custom = Object.entries(roleTags)
      .map(([key, tag]) => ({ level: Number(key), tag }))
      .filter(({ level }) => !named.some((entry) => entry.value === String(level)))
      .sort((a, b) => b.level - a.level)
      .map(({ level, tag }) => ({
        value: String(level),
        label: tag.name,
        disabled: level > ownLevel,
      }));
    const known = [...named, ...custom];
    if (known.some((option) => option.value === String(current))) return known;
    return [{ value: String(current), label: String(current) }, ...known];
  }

  function startEditRole(level: number): void {
    const tag = tagForLevel(roleTags, level);
    editingLevel = level;
    editingNewRole = false;
    roleLevelDraft = String(level);
    roleNameDraft = tag?.name ?? levelLabel(level);
    roleColorDraft = tag?.color ?? '';
    roleIconDraft = tag?.icon ?? '';
    roleLevelError = null;
    roleFailed = false;
  }

  function startAddRole(): void {
    editingLevel = null;
    editingNewRole = true;
    roleLevelDraft = '';
    roleNameDraft = '';
    roleColorDraft = '';
    roleIconDraft = '';
    roleLevelError = null;
    roleFailed = false;
  }

  function revealRoleEditor(node: HTMLElement): void {
    void editingLevel;
    node.scrollIntoView({ block: 'nearest' });
    node.querySelector('input')?.focus({ preventScroll: true });
  }

  function cancelEditRole(): void {
    editingLevel = null;
    editingNewRole = false;
  }

  async function saveRole(): Promise<void> {
    const target = roomId;
    if (!target || !editingRole || roleSaving) return;

    let level: number;
    if (editingNewRole) {
      const levelInput = parsePowerLevelInput(roleLevelDraft, ownLevel);
      if (!levelInput.valid) {
        const messages = {
          'not-a-number': i18n.t('room.permLevelNumberInvalid'),
          'out-of-range': i18n.t('room.permLevelNumberOutOfRange'),
          'exceeds-own': i18n.t('room.permLevelNumberExceedsOwn'),
        } as const;
        roleLevelError = messages[levelInput.reason];
        return;
      }
      level = levelInput.level;
    } else {
      if (editingLevel === null) return;
      level = editingLevel;
    }

    const name = roleNameDraft.trim();
    if (name === '') return;

    const color = /^#[0-9a-f]{6}$/i.test(roleColorDraft.trim()) ? roleColorDraft.trim() : null;

    roleSaving = true;
    roleFailed = false;
    try {
      data.roleTags = await core.rpc.members.setPowerLevelTag({
        room_id: target,
        level,
        tag: {
          name,
          color,
          icon: roleIconDraft.trim() || null,
        },
      });
      editingLevel = null;
      editingNewRole = false;
    } catch (error) {
      console.warn('[sable room] role tag save failed', error);
      roleFailed = true;
    } finally {
      roleSaving = false;
    }
  }

  async function removeRole(): Promise<void> {
    const target = roomId;
    const level = editingLevel;
    if (!target || level === null || roleSaving) return;

    roleSaving = true;
    roleFailed = false;
    try {
      data.roleTags = await core.rpc.members.setPowerLevelTag({
        room_id: target,
        level,
        tag: null,
      });
      editingLevel = null;
    } catch (error) {
      console.warn('[sable room] role tag remove failed', error);
      roleFailed = true;
    } finally {
      roleSaving = false;
    }
  }

  async function uploadRoleIcon(event: Event & { currentTarget: HTMLInputElement }): Promise<void> {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (!file || iconUploading) return;

    iconUploading = true;
    roleFailed = false;
    try {
      const upright = await uprightJpeg(file);
      const bytes = new Uint8Array(await upright.arrayBuffer());
      roleIconDraft = await core.media.uploadMedia(upright.type || 'image/*', bytes);
    } catch (error) {
      console.warn('[sable room] role icon upload failed', error);
      roleFailed = true;
    } finally {
      iconUploading = false;
    }
  }

  async function confirmRemoveRole(): Promise<void> {
    await removeRole();
    roleRemoveConfirm = false;
  }
</script>

<div class="section">
  {#if failed}
    <Alert variant="critical" role="alert">{i18n.t('room.permFailed')}</Alert>
  {/if}

  {#if loading && levels === null}
    <p class="settings-status" role="status"><Spinner small /></p>
  {:else if levels}
    {#if !canEdit}
      <Alert variant="info" role="status">{i18n.t('room.permReadOnly')}</Alert>
    {/if}
    {#if founders.length > 0}
      <SettingsSection
        headingId="room-perm-founders"
        title={i18n.t('room.permFounders')}
        description={i18n.t('room.permFoundersHint')}
      >
        <ul class="settings-rows">
          {#each founders as founder (founder)}
            <SettingsRow>
              {#snippet copy()}
                <MemberIdentityRow userId={founder} members={[]}>
                  {#snippet trailing()}
                    <span class="level">{founder}</span>
                  {/snippet}
                </MemberIdentityRow>
              {/snippet}
            </SettingsRow>
          {/each}
        </ul>
      </SettingsSection>
    {/if}

    <SettingsSection headingId="room-perm-roles" title={i18n.t('room.permRoles')}>
      <ul class="settings-rows">
        {#each roleLevels as level (level)}
          {@const tag = tagForLevel(roleTags, level)}
          <SettingsRow
            title={level === FOUNDER_POWER_LEVEL
              ? levelLabel(level)
              : `${levelLabel(level)} (${level})`}
          >
            {#if tag?.icon}<RoleTagIcon icon={tag.icon} class="role-icon" />{/if}
            {#if tag?.color}
              <span class="role-swatch" style:background-color={tag.color}></span>
            {/if}
            <IconButton
              variant="subtle"
              size="small"
              label={i18n.t('room.permRolePeek', { role: levelLabel(level) })}
              aria-expanded={peekLevel === level}
              onclick={() => {
                peekLevel = peekLevel === level ? null : level;
              }}
            >
              <ListChecksIcon />
            </IconButton>
            {#if canEditRoles && level <= ownLevel}
              <IconButton
                variant="subtle"
                size="small"
                label={i18n.t('room.permRoleEdit')}
                disabled={saving}
                onclick={() => startEditRole(level)}
              >
                <PencilIcon />
              </IconButton>
            {/if}
          </SettingsRow>
          {#if peekLevel === level}
            <li class="settings-form peek">
              {#each groups as group (group.label)}
                <div class="peek-group">
                  <p class="peek-title">{i18n.t(group.label)}</p>
                  <ul class="peek-items">
                    {#each group.items as item (item.label)}
                      {@const allowed = levelAt(levels, item.location) <= level}
                      <li class={{ denied: !allowed }}>
                        {#if allowed}<CheckIcon aria-hidden="true" />{:else}<XIcon
                            aria-hidden="true"
                          />{/if}
                        <span>{i18n.t(item.label)}</span>
                        <span class="screen-reader-only">
                          {allowed ? i18n.t('room.permAllowed') : i18n.t('room.permDenied')}
                        </span>
                      </li>
                    {/each}
                  </ul>
                </div>
              {/each}
            </li>
          {/if}
        {/each}
        {#if canEditRoles}
          <li class="settings-form role-add-row">
            <Button variant="secondary" onclick={startAddRole} disabled={saving}>
              {i18n.t('room.permRoleAdd')}
            </Button>
          </li>
        {/if}
      </ul>
    </SettingsSection>

    {#if editingRole}
      <SettingsSection headingId="room-perm-role-editor" title={i18n.t('room.permRoleEdit')}>
        <form
          class="settings-form"
          {@attach revealRoleEditor}
          onsubmit={(event) => {
            event.preventDefault();
            void saveRole();
          }}
        >
          {#if roleFailed}
            <Alert variant="critical" role="alert">{i18n.t('room.permFailed')}</Alert>
          {/if}
          {#if editingNewRole}
            <FormField fieldId="room-perm-role-level" label={i18n.t('room.permRoleLevel')}>
              <TextInput
                id="room-perm-role-level"
                inputmode="numeric"
                aria-invalid={roleLevelError ? 'true' : undefined}
                bind:value={roleLevelDraft}
                required
              />
              {#if roleLevelError}<p class="number-error" role="alert">{roleLevelError}</p>{/if}
            </FormField>
          {/if}
          <FormField fieldId="room-perm-role-name" label={i18n.t('room.permRoleName')}>
            <TextInput id="room-perm-role-name" bind:value={roleNameDraft} required />
          </FormField>
          <FormField fieldId="room-perm-role-color" label={i18n.t('room.permRoleColor')}>
            <div class="color-field">
              <input
                class="color-picker"
                type="color"
                aria-label={i18n.t('room.permRoleColorPick')}
                value={pickedColor}
                oninput={(event) => {
                  roleColorDraft = event.currentTarget.value;
                }}
              />
              <TextInput
                id="room-perm-role-color"
                class="color-text"
                bind:value={roleColorDraft}
                placeholder={i18n.t('room.permRoleColorHint')}
              />
            </div>
          </FormField>
          <FormField fieldId="room-perm-role-icon" label={i18n.t('room.permRoleIcon')}>
            <div class="icon-field">
              <span class="icon-preview" aria-hidden="true">
                {#if roleIconDraft}<RoleTagIcon icon={roleIconDraft} />{/if}
              </span>
              <TextInput
                id="room-perm-role-icon"
                class="icon-text"
                bind:value={roleIconDraft}
                placeholder="✨"
                autocomplete="off"
              />
              <ReactionPicker
                label={i18n.t('room.permRoleIconPick')}
                roomId={roomId ?? ''}
                triggerClass="btn btn-secondary btn-small"
                onPick={(key: string) => {
                  roleIconDraft = key;
                }}
              >
                {i18n.t('room.permRoleIconPick')}
              </ReactionPicker>
              <Button
                type="button"
                size="small"
                variant="secondary"
                loading={iconUploading}
                onclick={() => iconInput?.click()}
              >
                {i18n.t('room.permRoleIconUpload')}
              </Button>
              {#if roleIconDraft}
                <Button
                  type="button"
                  size="small"
                  variant="ghost"
                  onclick={() => {
                    roleIconDraft = '';
                  }}
                >
                  {i18n.t('room.permRoleIconClear')}
                </Button>
              {/if}
              <input
                bind:this={iconInput}
                use:nativeFilePicker
                class="icon-input"
                type="file"
                accept="image/*"
                tabindex="-1"
                aria-hidden="true"
                onchange={uploadRoleIcon}
              />
            </div>
          </FormField>
          <div class="actions">
            {#if editingHasTag}
              <Button
                type="button"
                variant="danger"
                onclick={() => {
                  roleRemoveConfirm = true;
                }}
                disabled={roleSaving}
              >
                {i18n.t('room.permRoleRemove')}
              </Button>
            {/if}
            <Button type="button" variant="ghost" onclick={cancelEditRole} disabled={roleSaving}>
              {i18n.t('common.cancel')}
            </Button>
            <Button type="submit" loading={roleSaving} disabled={roleNameDraft.trim() === ''}>
              {i18n.t('common.save')}
            </Button>
          </div>
        </form>
      </SettingsSection>
    {/if}

    <SettingsSection headingId="room-perm-member-list" title={i18n.t('room.memberListTitle')}>
      <ul class="settings-rows">
        <SettingsRow
          title={i18n.t('room.memberListRow')}
          description={i18n.t('room.memberListHint')}
        >
          <Select
            value={alwaysListedFrom === null ? 'default' : String(alwaysListedFrom)}
            aria-label={i18n.t('room.memberListRow')}
            disabled={!canEditMemberList || memberListSaving}
            items={memberListChoices}
            onValueChange={(next: string) => void saveMemberList(next)}
          />
        </SettingsRow>
      </ul>
      {#if memberListFailed}
        <div class="settings-form">
          <Alert variant="critical" role="alert">{i18n.t('room.memberListFailed')}</Alert>
        </div>
      {/if}
    </SettingsSection>

    {#if canEdit && syncSpaceId}
      <SettingsSection headingId="room-perm-sync" title={i18n.t('room.permSyncTitle')}>
        <ul class="settings-rows">
          <SettingsRow
            title={i18n.t('room.permSyncRow', { space: spaceName(syncSpaceId) })}
            description={i18n.t('room.permSyncHint')}
          >
            {#if syncSpaceIds.length > 1}
              <Select
                value={syncSpaceId}
                aria-label={i18n.t('room.permSyncSpace')}
                disabled={syncing}
                items={syncSpaceIds.map((spaceId) => ({
                  value: spaceId,
                  label: spaceName(spaceId),
                }))}
                onValueChange={(next: string) => {
                  syncChoice = next;
                }}
              />
            {/if}
            <Button
              variant="secondary"
              disabled={saving || syncing}
              onclick={() => {
                syncFailed = false;
                syncConfirm = true;
              }}
            >
              {i18n.t('room.permSync')}
            </Button>
          </SettingsRow>
        </ul>
      </SettingsSection>
    {/if}

    {#if canEdit && childIds.length > 0}
      <SettingsSection headingId="room-perm-children" title={i18n.t('room.permChildrenTitle')}>
        <ul class="settings-rows">
          <SettingsRow
            title={i18n.t('room.permChildrenRow', { count: childIds.length })}
            description={childResult
              ? i18n.t('room.permChildrenDone', {
                  count: childResult.updated,
                  skipped: childResult.skipped,
                })
              : i18n.t('room.permChildrenHint')}
          >
            <Button
              variant="secondary"
              disabled={saving || childSyncing}
              onclick={() => {
                childResult = null;
                childConfirm = true;
              }}
            >
              {i18n.t('common.apply')}
            </Button>
          </SettingsRow>
        </ul>
      </SettingsSection>
    {/if}

    {#each groups as group (group.label)}
      <SettingsSection headingId={`room-perm-${group.label}`} title={i18n.t(group.label)}>
        <ul class="settings-rows">
          {#each group.items as item (item.label)}
            {@const level = levelAt(levels, item.location)}
            {@const tag = tagForLevel(roleTags, level)}
            <SettingsRow title={i18n.t(item.label)}>
              {#if canEdit && level <= ownLevel}
                <Select
                  value={String(level)}
                  aria-label={i18n.t(item.label)}
                  disabled={saving}
                  items={options(level)}
                  onValueChange={(next: string) => {
                    void setLevel(item.location, Number(next));
                  }}
                />
              {:else if tag}
                <span class="role-chip">
                  <span
                    class="role-swatch"
                    style:background-color={tag.color ?? undefined}
                    aria-hidden="true"
                  ></span>
                  {#if tag.icon}<RoleTagIcon icon={tag.icon} class="role-icon" />{/if}
                  <span class="role-name">{tag.name} ({level})</span>
                </span>
              {:else}
                <span class="level">{levelLabel(level)}</span>
              {/if}
            </SettingsRow>
          {/each}
        </ul>
      </SettingsSection>
    {/each}
  {/if}
</div>

<ConfirmDialog
  bind:open={syncConfirm}
  title={i18n.t('room.permSyncConfirm', { space: syncSpaceId ? spaceName(syncSpaceId) : '' })}
  description={i18n.t('room.permSyncConfirmHint')}
  confirmLabel={i18n.t('room.permSync')}
  busy={syncing}
  error={syncFailed ? i18n.t('room.permFailed') : null}
  onConfirm={() => void syncFromSpace()}
/>

<ConfirmDialog
  bind:open={childConfirm}
  title={i18n.t('room.permChildrenConfirm', { count: childIds.length })}
  description={i18n.t('room.permSyncConfirmHint')}
  confirmLabel={i18n.t('common.apply')}
  busy={childSyncing}
  onConfirm={() => void syncChildren()}
/>

<ConfirmDialog
  bind:open={roleRemoveConfirm}
  title={i18n.t('room.permRoleRemoveConfirm')}
  description={i18n.t('room.permRoleRemoveHint')}
  confirmLabel={i18n.t('room.permRoleRemove')}
  busy={roleSaving}
  onConfirm={() => void confirmRemoveRole()}
/>

<style>
  .section {
    display: grid;
    gap: var(--space-600);
  }

  .level {
    color: var(--surface-var-on-container);
    font-size: var(--font-size-small);
  }

  .role-chip {
    align-items: center;
    background-color: var(--surface-container);
    border: var(--border-width) solid var(--surface-container-line);
    border-radius: var(--radius-pill);
    color: var(--surface-on-container);
    display: inline-flex;
    gap: var(--space-200);
    padding: var(--space-100) var(--space-300);
  }

  .role-swatch {
    background-color: var(--surface-var-on-container);
    border-radius: var(--radius-pill);
    flex: 0 0 auto;
    height: 0.6rem;
    width: 0.6rem;
  }

  .role-add-row {
    justify-items: start;
  }

  .role-name {
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-medium);
  }

  :global(.role-icon) {
    font-size: var(--font-size-small);
  }

  .peek {
    gap: var(--space-300);
  }

  .peek-group {
    display: grid;
    gap: var(--space-100);
  }

  .peek-title {
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-bold);
    margin: 0;
  }

  .peek-items {
    display: grid;
    gap: var(--space-100);
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .peek-items li {
    align-items: center;
    display: flex;
    font-size: var(--font-size-small);
    gap: var(--space-200);
  }

  .peek-items li :global(svg) {
    color: var(--success-main);
    flex: 0 0 auto;
    height: var(--icon-size-small);
    width: var(--icon-size-small);
  }

  .peek-items li.denied {
    color: var(--surface-var-on-container);
  }

  .peek-items li.denied :global(svg) {
    color: var(--crit-main);
  }

  .icon-field {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-200);
  }

  .icon-field :global(.icon-text) {
    flex: 1 1 8rem;
    min-width: 0;
  }

  .icon-preview {
    align-items: center;
    background: var(--surface-container);
    border: var(--border-width) solid var(--surface-container-line);
    border-radius: var(--radius);
    color: var(--surface-on-container);
    display: inline-flex;
    font-size: var(--font-size-heading);
    height: var(--control-height-medium);
    justify-content: center;
    width: var(--control-height-medium);
  }

  .color-field {
    align-items: center;
    display: flex;
    gap: var(--space-200);
  }

  .color-field :global(.color-text) {
    flex: 1 1 auto;
    min-width: 0;
  }

  .color-picker {
    background: none;
    block-size: var(--control-height-medium);
    border: var(--border-width) solid var(--surface-container-line);
    border-radius: var(--radius);
    cursor: pointer;
    flex: 0 0 auto;
    inline-size: var(--control-height-medium);
    padding: var(--space-050);
  }

  .color-picker:focus-visible {
    outline: var(--focus-ring-width) solid var(--focus-ring);
  }

  .icon-input {
    height: 0;
    opacity: 0;
    position: absolute;
    width: 0;
  }

  .number-error {
    color: var(--crit-main);
    font-size: var(--font-size-small);
    margin: 0;
  }

  .actions {
    display: flex;
    gap: var(--space-300);
    justify-content: flex-end;
  }
</style>
